import React, { useState, useEffect } from "react";
import { Check, Cpu, Loader2 } from "lucide-react";

/**
 * Modell-Karte fuer die Einstellungen. Bewusst eigene Komponente statt Umbau am
 * Cutter-Block: der laeuft und wird nicht angefasst (03.09.2026).
 *
 * laden()  -> {aktiv, endpoint, laufende_runs, modelle:[{key,label,anbieter,preis,notiz,bereit}],
 *               agenten:[{key,label,pin,effektiv}]}   (agenten seit 04.09.2026, optional)
 * setzen(key, agent?) -> {success}
 *
 * Pro Agent (04.09.2026): jeder Bot kann sein eigenes Modell haben. "wie Morpheus"
 * heisst kein Pin, der Bot folgt dem grossen Schalter oben. Anlass: KOBOLD auf
 * Mistral erfand eine Lexware-Fehlermeldung; der Geld-Agent braucht ein Modell,
 * das nicht erfindet, waehrend Smalltalk billig laufen darf.
 */
export default function ModellWahl({ titel, laden, setzen, hinweis }) {
  const [llm, setLlm] = useState(null);
  const [fehler, setFehler] = useState("");
  const [wechselt, setWechselt] = useState("");

  const neu = () => laden().then(setLlm).catch((e) => setFehler(e.message));
  useEffect(() => { setFehler(""); neu(); }, []);

  const waehlen = async (key) => {
    const m = (llm?.modelle || []).find((x) => x.key === key);
    if (wechselt || key === llm?.aktiv || !m) return;
    if (llm?.laufende_runs?.length) return;
    if (!window.confirm(`Modell auf „${m.label}" (${m.anbieter}) umstellen?\n\nGilt ab dem nächsten Turn. Ein laufender Turn bleibt auf dem alten Modell.`)) return;
    setFehler(""); setWechselt(key);
    try {
      const r = await setzen(key);
      if (!r?.success) setFehler(r?.error || "Umschalten fehlgeschlagen");
      await neu();
    } catch (e) { setFehler(e.message); }
    setWechselt("");
  };

  const labelFuer = (key) => (llm?.modelle || []).find((m) => m.key === key)?.label || key || "?";
  const agentWaehlen = async (agent, key) => {
    const a = (llm?.agenten || []).find((x) => x.key === agent);
    if (wechselt || !a || (a.pin || "") === key) return;
    const ziel = key ? `„${labelFuer(key)}"` : `„wie Morpheus"`;
    if (!window.confirm(`${a.label} auf ${ziel} stellen?\n\nGilt ab dem nächsten Lauf. Ein laufender Lauf bleibt auf dem alten Modell.`)) return;
    setFehler(""); setWechselt(`agent:${agent}`);
    try {
      const r = await setzen(key, agent);
      if (!r?.success) setFehler(r?.error || "Umstellen fehlgeschlagen");
      await neu();
    } catch (e) { setFehler(e.message); }
    setWechselt("");
  };

  return (
    <>
      <div className="eyebrow mb-2">{titel}</div>
      <div className="grid grid-cols-2 gap-2 mb-1">
        {(llm?.modelle || []).map((m) => {
          const aktiv = m.key === llm?.aktiv;
          const laeuft = !!llm?.laufende_runs?.length;
          const laedt = wechselt === m.key;
          return (
            <button key={m.key} onClick={() => waehlen(m.key)}
              disabled={!m.bereit || !!wechselt || laeuft}
              title={!m.bereit ? "Kein API-Key hinterlegt" : `${m.anbieter} · ${m.preis}${m.notiz ? `\n${m.notiz}` : ""}`}
              className={`flex items-center gap-2.5 rounded-xl border p-2.5 transition text-left ${aktiv ? "border-[var(--signal)]/60 bg-white/[0.05]" : "border-white/10 hover:border-white/25"} ${!m.bereit || wechselt || laeuft ? "opacity-50 cursor-not-allowed" : ""}`}>
              <span className="w-8 h-8 rounded-lg shrink-0 grid place-items-center border border-white/10 bg-white/[0.04] text-[var(--signal)]">
                {laedt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-white truncate">{m.label}</span>
                <span className="block text-ether text-[10px] truncate">{m.anbieter}</span>
                <span className="block text-ether text-[10px] truncate opacity-70">{m.preis}</span>
              </span>
              {aktiv && !laedt && <Check className="w-4 h-4 text-[var(--signal)] shrink-0" />}
            </button>
          );
        })}
      </div>
      <div className="text-[10px] mb-3">
        {fehler ? <span className="text-[var(--flare)]">{fehler}</span>
          : wechselt ? <span className="text-ether">wird umgestellt …</span>
          : llm ? <span className="text-ether">{hinweis} Endpoint: {llm.endpoint}</span>
          : <span className="text-ether">lädt …</span>}
      </div>
      {!!llm?.agenten?.length && (
        <div className="mb-5 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-ether mb-1"
            title="Kein Pin = der Bot folgt dem Schalter oben. Ein Pin gilt nur für diesen Bot.">
            Pro Agent
          </div>
          {llm.agenten.map((a) => {
            const laedt = wechselt === `agent:${a.key}`;
            return (
              <div key={a.key} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 px-3 py-2">
                <span className="text-sm text-white">{a.label}</span>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-ether text-[10px] truncate">
                    {laedt ? "wird umgestellt …" : `läuft auf ${labelFuer(a.effektiv)}`}
                  </span>
                  <select value={a.pin || ""} disabled={!!wechselt}
                    onChange={(e) => agentWaehlen(a.key, e.target.value)}
                    className="bg-black/30 border border-white/10 rounded-lg text-xs text-white px-2 py-1 disabled:opacity-50">
                    <option value="">wie Morpheus</option>
                    {(llm.modelle || []).map((m) => (
                      <option key={m.key} value={m.key} disabled={!m.bereit}>{m.label}</option>
                    ))}
                  </select>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
